// src/pages/feedback/components/feedbackVisuals.ts
//
// 피드백 칩의 색·라벨 단일 소스. statusVisuals.ts 의 "단일 소스" 관례를 따른다.
//
// 상태 칩과 유형 칩은 **같은 행에 동시에** 놓이므로 채널을 형태로 분리한다:
//   · 상태 칩 = filled soft (옅은 틴트 배경 + 6px 도트) — StatusChip.tsx 규격
//   · 유형 칩 = outlined    (흰 배경 + 의미색 보더/텍스트, 도트 없음)
//
// 색 배정 근거(총괄 확정):
//  1) stop(#B12B2B) 계열은 **유형 bug 전용**으로 예약했다. declined(보류)를
//     stop 으로 칠하면 "버그 + 보류" 글에서 붉은 칩이 두 개 겹쳐 색 위계가
//     무너진다. 보류는 오류가 아니라 "더 이상 흐르지 않음"이므로 StatusChip 의
//     draft/closed 관례를 따라 중립(#F2F4F5)을 쓴다.
//  2) 상태 색은 총괄 확정 #5 를 정본으로 한다 —
//     open=steel 틴트 / in_progress=amber 틴트 / done=signal 틴트 / declined=중립.
//     (프론트 설계서의 색표는 open=amber / in_progress=steel 이었으나 확정문이
//      우선한다. "접수"는 아직 손대지 않은 대기 상태라 중립에 가까운 steel,
//      "검토중"은 지금 사람이 붙어 움직이는 상태라 주의를 끄는 amber 다.)
//     improvement(steel outlined) 와 open(steel filled) 는 같은 계열이지만
//     면 채움 유무 + 도트 유무로 구분된다 — statusVisuals.ts 가 명시한
//     "칩 채널과 면 채움 채널은 다른 채널" 원칙과 같다.
//  3) 색만으로 의미를 전달하지 않는다 — 두 칩 모두 한글 라벨을 항상 병기한다.
//  4) 액센트 #E2451C(카고 오렌지)는 상태·유형 어디에도 쓰지 않는다.
//     액센트는 "액션"(글쓰기/등록/선택된 필터) 전용이라는 theme.ts 규칙을 지킨다.

import { designTokens } from "../../../theme.ts";
import type { FeedbackStatus, FeedbackType } from "../../../contracts/feedback/types";

// 의미색은 theme.ts 의 토큰을 참조한다 — StatusChip.tsx 와 같은 관례.
// 틴트 배경/보더는 토큰이 아니므로(팔레트에 없음) 리터럴로 둔다.
const T = designTokens;

export interface ChipVisual {
  /** 한글 라벨 — 색과 무관하게 항상 함께 렌더한다. */
  ko: string;
  bg: string;
  fg: string;
  /** 상태 칩에만 있다. 유형 칩은 도트를 쓰지 않는다. */
  dot?: string;
  border: string;
}

/** 유형 — outlined(흰 바탕 + 의미색 보더/텍스트). */
export const TYPE_VISUALS: Record<FeedbackType, ChipVisual> = {
  bug:         { ko: "문제점", bg: "#FFFFFF", fg: T.stop, border: "#F0D6D6" },
  improvement: { ko: "개선",   bg: "#FFFFFF", fg: "#0B4A59", border: "#CFE1E6" },
  other:       { ko: "기타",   bg: "#FFFFFF", fg: "#6B7178", border: "#E4E7EA" },
};

/** 상태 — filled soft(옅은 틴트 + 도트). StatusChip.tsx 와 동일 규격. */
export const STATUS_VISUALS: Record<FeedbackStatus, ChipVisual> = {
  open:        { ko: "접수",   bg: "#E6F0F2", fg: "#0B4A59", dot: T.steel,  border: "#CFE1E6" },
  in_progress: { ko: "검토중", bg: "#FCF3E4", fg: "#8A5C12", dot: T.amber,  border: "#F1E0C2" },
  done:        { ko: "완료",   bg: "#E7F2EA", fg: "#1F6535", dot: T.signal, border: "#CFE4D6" },
  declined:    { ko: "보류",   bg: "#F2F4F5", fg: "#4A5057", dot: "#9AA0A6", border: "#E4E7EA" },
};

export const TYPE_ORDER: FeedbackType[] = ["bug", "improvement", "other"];
export const STATUS_ORDER: FeedbackStatus[] = [
  "open",
  "in_progress",
  "done",
  "declined",
];

/**
 * 폴백이 있는 룩업 — statusVisuals.ts 의 rentalVisual() 관례와 동일.
 * 서버가 알 수 없는 값을 내려줘도 칩이 undefined 로 깨지지 않는다.
 */
export const typeVisual = (t: FeedbackType): ChipVisual =>
  TYPE_VISUALS[t] ?? TYPE_VISUALS.other;

export const statusVisual = (s: FeedbackStatus): ChipVisual =>
  STATUS_VISUALS[s] ?? STATUS_VISUALS.open;

/** 스낵바 문구 등에서 쓰는 한글 라벨. */
export const typeLabel = (t: FeedbackType): string => typeVisual(t).ko;
export const statusLabel = (s: FeedbackStatus): string => statusVisual(s).ko;
